// ───────────────────────────────────────────────────────────
//  BotKnowledgePanel — Módulo "Asistente IA" para el CEO Dashboard
//
//  Cómo integrarlo en src/pages/CeoPage.js:
//
//  1) Pegá este archivo como src/components/BotKnowledgePanel.js
//     (o pegá el componente dentro de CeoPage.js si preferís un solo archivo).
//
//  2) En CeoPage.js, importá arriba:
//        import BotKnowledgePanel from '../components/BotKnowledgePanel';
//
//  3) Dentro del render de CeoPage, donde quieras que aparezca la sección
//     (por ejemplo al final del dashboard), agregá:
//        <BotKnowledgePanel />
//
//  Usa los endpoints que ya están vivos en el backend:
//     POST   /api/bot/docs/extract   (admin) → sube PDF base64, la IA extrae texto
//     GET    /api/bot/docs           (admin) → lista documentos
//     DELETE /api/bot/docs/:id       (admin) → borra documento
//     GET    /api/bot/stats          (admin) → { docs, chunks }
//     POST   /api/bot/chat           (público) → probar el bot
//
//  Mismo patrón de auth que el resto del sistema:
//     token = localStorage.getItem('nebb_token'); header Authorization Bearer.
// ───────────────────────────────────────────────────────────
import React, { useState, useEffect, useRef } from 'react';
import { Bot, Upload, Trash2, FileText, Send, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const RAMOS = ['', 'auto', 'hogar', 'alquiler', 'vida', 'empresa', 'reaseguros', 'siniestros', 'mascotas', 'general'];

// Header con token, igual que el resto del sistema.
function authHeaders(extra = {}) {
  const token = localStorage.getItem('nebb_token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...extra };
}

// Lee un File como base64 puro (sin el prefijo data:...;base64,).
function fileABase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1]);
    r.onerror = () => reject(new Error('No se pudo leer el archivo'));
    r.readAsDataURL(file);
  });
}

export default function BotKnowledgePanel() {
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState({ docs: 0, chunks: 0 });
  const [cargando, setCargando] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [msg, setMsg] = useState(null); // { tipo:'ok'|'error', texto }
  const [company, setCompany] = useState('');
  const [category, setCategory] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Mini-chat de prueba
  const [chat, setChat] = useState([]); // {role, content}
  const [pregunta, setPregunta] = useState('');
  const [pensando, setPensando] = useState(false);

  const aviso = (tipo, texto) => { setMsg({ tipo, texto }); setTimeout(() => setMsg(null), 5000); };

  async function cargarLista() {
    setCargando(true);
    try {
      const [rDocs, rStats] = await Promise.all([
        fetch('/api/bot/docs', { headers: authHeaders() }),
        fetch('/api/bot/stats', { headers: authHeaders() }),
      ]);
      if (rDocs.ok) setDocs((await rDocs.json()).docs || []);
      if (rStats.ok) setStats(await rStats.json());
    } catch { aviso('error', 'No se pudo cargar la lista de documentos.'); }
    setCargando(false);
  }

  useEffect(() => { cargarLista(); }, []);

  async function subirArchivos(fileList) {
    const files = Array.from(fileList).filter(f => f.type === 'application/pdf');
    if (!files.length) { aviso('error', 'Solo se aceptan archivos PDF.'); return; }
    setSubiendo(true);
    let ok = 0;
    for (const file of files) {
      try {
        const pdfBase64 = await fileABase64(file);
        const res = await fetch('/api/bot/docs/extract', {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify({
            filename: file.name,
            title: file.name.replace(/\.pdf$/i, ''),
            company: company || null,
            category: category || null,
            pdfBase64,
          }),
        });
        const data = await res.json();
        if (res.ok) ok++;
        else aviso('error', `"${file.name}": ${data.error || 'error al procesar'}`);
      } catch (e) {
        aviso('error', `"${file.name}": ${e.message}`);
      }
    }
    setSubiendo(false);
    if (ok) aviso('ok', `${ok} documento${ok !== 1 ? 's' : ''} cargado${ok !== 1 ? 's' : ''} y procesado${ok !== 1 ? 's' : ''} por la IA.`);
    cargarLista();
  }

  async function borrar(id, title) {
    if (!window.confirm(`¿Borrar "${title}" de la base de conocimiento del bot?`)) return;
    try {
      const res = await fetch(`/api/bot/docs/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) { aviso('ok', 'Documento borrado.'); cargarLista(); }
      else aviso('error', 'No se pudo borrar.');
    } catch { aviso('error', 'No se pudo borrar.'); }
  }

  async function preguntar() {
    const q = pregunta.trim();
    if (!q || pensando) return;
    const nuevoChat = [...chat, { role: 'user', content: q }];
    setChat(nuevoChat);
    setPregunta('');
    setPensando(true);
    try {
      const res = await fetch('/api/bot/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: nuevoChat, meta: { canal: 'prueba-ceo' } }),
      });
      const data = await res.json();
      setChat([...nuevoChat, { role: 'assistant', content: data.reply || data.error || 'Sin respuesta.', fuentes: data.fuentes }]);
    } catch {
      setChat([...nuevoChat, { role: 'assistant', content: 'Error de conexión con el asistente.' }]);
    }
    setPensando(false);
  }

  return (
    <div className="card" style={{ marginTop: 24 }}>
      {/* Encabezado */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: '#7c6cf8', display: 'grid', placeItems: 'center' }}>
          <Bot size={19} color="#fff" />
        </div>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 700, margin: 0 }}>Asistente IA — Base de conocimiento</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted, #667)', margin: 0 }}>
            {stats.docs} documento{stats.docs !== 1 ? 's' : ''} · {stats.chunks} fragmento{stats.chunks !== 1 ? 's' : ''} que el bot puede consultar
          </p>
        </div>
      </div>

      {msg && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0', padding: '10px 14px', borderRadius: 8, fontSize: 13.5,
          background: msg.tipo === 'ok' ? '#ecfdf5' : '#fef2f2', color: msg.tipo === 'ok' ? '#065f46' : '#991b1b',
          border: `1px solid ${msg.tipo === 'ok' ? '#a7f3d0' : '#fecaca'}`,
        }}>
          {msg.tipo === 'ok' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {msg.texto}
        </div>
      )}

      {/* Zona de carga */}
      <div style={{ display: 'flex', gap: 10, margin: '16px 0 12px', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ flex: 1, minWidth: 160, margin: 0 }}>
          <label className="form-label">Compañía (opcional)</label>
          <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Ej: SURA, Porto, BSE…" />
        </div>
        <div className="form-group" style={{ flex: 1, minWidth: 160, margin: 0 }}>
          <label className="form-label">Categoría (opcional)</label>
          <select value={category} onChange={e => setCategory(e.target.value)}>
            {RAMOS.map(r => <option key={r} value={r}>{r ? r[0].toUpperCase() + r.slice(1) : '— sin categoría —'}</option>)}
          </select>
        </div>
      </div>

      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); subirArchivos(e.dataTransfer.files); }}
        onClick={() => !subiendo && inputRef.current && inputRef.current.click()}
        style={{
          border: `2px dashed ${dragOver ? 'var(--accent, #7c6cf8)' : 'var(--border)'}`, borderRadius: 12, padding: '28px 20px',
          textAlign: 'center', cursor: subiendo ? 'wait' : 'pointer', background: dragOver ? 'var(--bg-card-hover)' : 'var(--bg-input)',
          transition: '.15s',
        }}
      >
        <input ref={inputRef} type="file" accept="application/pdf" multiple style={{ display: 'none' }}
          onChange={e => subirArchivos(e.target.files)} />
        {subiendo ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#7c6cf8', fontWeight: 600 }}>
            <Loader2 size={20} className="spin" /> La IA está leyendo y procesando el PDF…
          </div>
        ) : (
          <>
            <Upload size={26} color="#7c6cf8" />
            <p style={{ margin: '8px 0 2px', fontWeight: 600, fontSize: 15 }}>Arrastrá un PDF acá o hacé click para elegir</p>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Condiciones, beneficios, tablas de coberturas… La IA extrae el texto sola.</p>
          </>
        )}
      </div>

      {/* Lista de documentos */}
      <div style={{ marginTop: 18 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
          Documentos cargados
        </h3>
        {cargando ? (
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>Cargando…</p>
        ) : docs.length === 0 ? (
          <p style={{ fontSize: 13.5, color: 'var(--text-muted)' }}>Todavía no hay documentos. Subí el primero para que el bot tenga información oficial de NEBB.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {docs.map(d => (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-card)',
              }}>
                <FileText size={17} color="#7c6cf8" style={{ flex: 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {[d.company, d.category, `${d.chunks} fragmentos`].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <button onClick={() => borrar(d.id, d.title)} className="btn btn-secondary btn-sm" style={{ padding: '6px 8px', flex: 'none' }} title="Borrar">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Mini-chat de prueba */}
      <div style={{ marginTop: 22, borderTop: '1px solid var(--border)', paddingTop: 18 }}>
        <h3 style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 10 }}>
          Probar el bot
        </h3>
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ maxHeight: 260, overflowY: 'auto', padding: 14, background: 'var(--bg-input)', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {chat.length === 0 && (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', margin: '18px 0' }}>
                Escribí una consulta para ver cómo responde el bot con la info cargada.
              </p>
            )}
            {chat.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                <div style={{
                  padding: '9px 13px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                  background: m.role === 'user' ? 'var(--accent, #7c6cf8)' : 'var(--bg-card)', color: m.role === 'user' ? '#fff' : 'var(--text-primary)',
                  border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                }}>{m.content}</div>
                {m.fuentes && m.fuentes.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 3 }}>Fuentes: {m.fuentes.join(', ')}</div>
                )}
              </div>
            ))}
            {pensando && (
              <div style={{ alignSelf: 'flex-start', color: 'var(--text-muted)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Loader2 size={14} className="spin" /> pensando…
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--border)', background: 'var(--bg-card)' }}>
            <input
              value={pregunta}
              onChange={e => setPregunta(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') preguntar(); }}
              placeholder="Ej: ¿Qué beneficios tiene la garantía de alquiler de SURA?"
              style={{ flex: 1 }}
            />
            <button onClick={preguntar} disabled={pensando} className="btn btn-primary btn-sm" style={{ padding: '8px 14px' }}>
              <Send size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* animación del spinner, por si no está global */}
      <style>{`.spin{animation:nebbspin 1s linear infinite}@keyframes nebbspin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
